import React from "react";
import { eachDay } from "date-fns";
import { map } from "ramda";
import styled from "styles/styled_components";

import ScheduleTableHeader from "components/schedule/schedule_table_header";
import ScheduleTablePersonRow from "components/schedule/schedule_table_person_row";
import useSelfRoute from "helpers/hooks/useSelfRoute";
import { IPerson, ISchedule } from "types/schedules";

const StyledTable = styled.table`
  table-layout: fixed;
  width: 100%;
`;

interface IScheduleWeekView {
  schedule: ISchedule;
  startDate: Date;
  endDate: Date;
  interactiveView: boolean;
  tableRef?: React.MutableRefObject<HTMLTableElement | null>;
  clearFilters?: () => void;
}

const StyledEmptyRow = styled.tr`
  font-family: ${(props) => props.theme.text.primaryFamily};
  width: 100%;
`;

const StyledEmptyTextCell = styled.td`
  margin: ${(props) => props.theme.spacing.medium};
  position: absolute;
`;

const StyledClearFiltersLink = styled.a`
  color: ${(props) => props.theme.colors.uiLink};
  font-family: ${(props) => props.theme.text.mediumFamily};
  cursor: pointer;
`;

function ScheduleWeekView({
  schedule,
  startDate,
  endDate,
  interactiveView,
  tableRef,
  clearFilters,
}: IScheduleWeekView) {
  // Notify iframe parent of self route
  useSelfRoute("/workforce/schedule");

  const scheduleDates: Date[] = eachDay(startDate, endDate);
  let tableBody;
  const { people } = schedule;
  if (!interactiveView || people.length > 1) {
    tableBody = map((person: IPerson) => {
      return (
        <ScheduleTablePersonRow
          key={person.assignee
            .map((assignee) => assignee.uuid)
            .unwrapOr("unassigned")}
          person={person}
          scheduleDates={scheduleDates}
          interactiveView={interactiveView}
        />
      );
    }, schedule.people);
  } else {
    const clearFiltersLink = clearFilters ? (
      <StyledClearFiltersLink onClick={clearFilters}>
        clear filters.
      </StyledClearFiltersLink>
    ) : (
      "clear filters"
    );
    tableBody = (
      <StyledEmptyRow>
        <StyledEmptyTextCell colSpan={2}>
          <span>No results. Adjust or {clearFiltersLink}</span>
        </StyledEmptyTextCell>
      </StyledEmptyRow>
    );
  }

  return (
    <StyledTable ref={tableRef}>
      <thead>
        <ScheduleTableHeader
          days={scheduleDates}
          interactiveView={interactiveView}
        />
      </thead>
      <tbody>{tableBody}</tbody>
    </StyledTable>
  );
}

export default React.memo(ScheduleWeekView);
